import { useState, useEffect, useCallback, useRef } from 'react';
import { DocumentData } from 'firebase/firestore';

import { getLatestMessages, getOlderMessages, listenToNewMessages } from '@/slw/shared/lib/firebase/utils';
import { Message } from '@/slw/entities/chat';

export function useChatMessages(chatId: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const firstDocRef = useRef<DocumentData | null>(null);
  const lastDocRef = useRef<DocumentData | null>(null);
  const unsubscribeRef = useRef<() => void>(null);

  const loadInitialMessages = useCallback(async () => {
    if (!chatId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await getLatestMessages(chatId, 10);

      setMessages(result.messages);
      firstDocRef.current = result.firstDoc;
      lastDocRef.current = result.lastDoc;
      setHasMore(result.hasMore);

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }

      unsubscribeRef.current = listenToNewMessages(chatId, result.lastDoc, (newMessages, latestDoc) => {
        setMessages((prev) => {
          const existingIds = new Set(prev.map((msg) => msg.id));
          const uniqueNewMessages = newMessages.filter((msg) => !existingIds.has(msg.id));

          if (uniqueNewMessages.length === 0) return prev;

          return [...prev, ...uniqueNewMessages];
        });

        if (latestDoc) {
          lastDocRef.current = latestDoc;
        }
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [chatId]);

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  const loadMoreMessages = useCallback(async () => {
    if (!chatId || loadingMore || !hasMore || !firstDocRef.current) return;

    setLoadingMore(true);

    try {
      const result = await getOlderMessages(chatId, firstDocRef.current, 10);

      await sleep(1000);

      if (result.messages.length > 0) {
        setMessages((prev) => [...result.messages, ...prev]);
        firstDocRef.current = result.firstDoc;
      }

      setHasMore(result.hasMore);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }, [chatId, loadingMore, hasMore]);

  useEffect(() => {
    loadInitialMessages();
  }, [chatId, loadInitialMessages]);

  return {
    messages,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMoreMessages,
    refresh: loadInitialMessages,
  };
}
